/**
 * Post-Round-96 Execution Loop — useAutoGenerate hook.
 *
 * Called once from every asset-generating bot page's mount.
 *
 *   * When `?auto_generate=1` is NOT present → hook is a no-op;
 *     the bot page shows its normal input form.
 *   * When it IS present → hook fetches the customer's existing
 *     business context (Business Memory) and invokes the
 *     caller-supplied generator once. The customer lands on
 *     PREPARED work instead of a blank form. Matches the
 *     "preparation over instruction" principle.
 *
 * The hook DOES NOT own the generator itself — every bot page
 * already has `botRunsAPI.create({ bot_type, inputs })`. We just
 * assemble ``inputs`` from existing memory and fire that call.
 *
 * Single-fire per mount, guarded by useRef so React 18 double-
 * mount doesn't run the generator twice.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/utils/api';
import useDeepLinkParams from './useDeepLinkParams';

/**
 * @param {object} config
 * @param {string} config.botType - "content" | "money" | "growth"
 *   | "business_builder".
 * @param {(memory: object, deepLink: object) => object} config.buildInputs
 *   - Called with the fetched business memory + deep link params;
 *   must return the ``inputs`` payload for `botRunsAPI.create`.
 * @param {(response: object) => void} config.onResult - Called
 *   with the generator response (`{data: {id, outputs, status}}`
 *   shape from the existing `botRunsAPI.create`).
 * @param {boolean} [config.enabled=true] - Set to false to disable
 *   the auto-run entirely (used by the feature-flag gate).
 * @returns {{
 *   isPreparing: boolean,
 *   assetKind: string|null,
 *   error: object|null,
 * }}
 */
export default function useAutoGenerate({
  botType,
  buildInputs,
  onResult,
  enabled = true,
}) {
  const deepLink = useDeepLinkParams();
  const firedRef = useRef(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState(null);

  const shouldRun = Boolean(
    enabled
      && deepLink.hasDeepLink
      && deepLink.mode === 'create'
      && (new URLSearchParams(window.location.search)).get('auto_generate') === '1',
  );

  const run = useCallback(async () => {
    if (firedRef.current) return;
    firedRef.current = true;
    setIsPreparing(true);
    setError(null);
    try {
      // Round 102 (2026-07-24) — `api` axios instance already
      // has `baseURL = ${REACT_APP_BACKEND_URL}/api`, so paths
      // MUST NOT include the `/api` prefix or we get a doubled
      // `/api/api/...` 404. The endpoint returns a wrapper
      // `{ has_memory, memory: {...}, hydrated_from }` — unwrap
      // to the inner memory object the bot pages actually read.
      const memResp = await api
        .get('/business-memory/profile')
        .catch(() => ({ data: {} }));
      const memBody = memResp?.data || {};
      const memory = memBody.memory || memBody || {};
      const inputs = buildInputs(memory, deepLink);
      const resp = await api.post('/bot-runs', {
        bot_type: botType,
        inputs,
      });
      if (onResult) onResult(resp);
    } catch (err) {
      setError(err);
    } finally {
      setIsPreparing(false);
    }
  }, [botType, buildInputs, onResult, deepLink]);

  useEffect(() => {
    if (shouldRun) run();
    // shouldRun is derived; run() is memoised — this only fires on
    // the mount that actually carries auto_generate=1.
  }, [shouldRun, run]);

  return {
    isPreparing,
    assetKind: deepLink.asset_type || null,
    error,
  };
}
